import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { message } from 'antd';
import { useAdminAuth } from '../hooks/useAdminAuth';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  status: string;
  phone?: string;
  permissions?: string[];
  isActive: boolean;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface AdminContextType {
  user: User | null;
  isAdmin: boolean;
  loading: boolean;
  error: string | null;
  refreshAuth: () => Promise<void>;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

interface AdminProviderProps {
  children: ReactNode;
}

export const AdminProvider: React.FC<AdminProviderProps> = ({ children }) => {
  const { isAdmin, user, loading, error } = useAdminAuth();
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const refreshAuth = async () => {
    setRefreshTrigger(prev => prev + 1);
  };

  useEffect(() => {
    if (error && !loading) {
      console.error('Admin auth error:', error);
    }
  }, [error, loading]);

  const contextValue: AdminContextType = {
    user,
    isAdmin,
    loading,
    error,
    refreshAuth,
  };

  return (
    <AdminContext.Provider value={contextValue}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = (): AdminContextType => {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

export default AdminContext;
